import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Input,
  InputNumber,
  Icon,
  Modal,
  Row,
  Select,
  Table,
  Tabs,
  Tag,
  Tooltip,
  message
} from 'antd';
import moment from 'moment';
import {
  createAdjustmentVoucher,
  getAdjustmentVouchers,
  getInventorySnapshot,
  getLocations,
  getSparePurchaseHistory,
  getSpareMaster,
  getStockTakingVouchers,
  subscribeToSparesStore
} from './sparesMockStore';

const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

const tooltipLabel = (label, tip) => (
  <span className="spares-tooltip-label">
    {label}
    <Tooltip title={tip}>
      <Icon type="info-circle" className="spares-tooltip-icon" />
    </Tooltip>
  </span>
);

const getVoucherIdFromHash = () => {
  const hash = window.location.hash || '';
  const query = hash.split('?')[1];
  if (!query) return null;
  const params = new URLSearchParams(query);
  return params.get('voucherId');
};

const formatCurrency = (value) => `Rs ${Number(value || 0).toFixed(2)}`;

const getFallbackPurchaseRate = (purchaseHistory, spareId, locationId) => {
  const matchingSpareHistory = purchaseHistory.filter((entry) => entry.spareId === spareId);
  if (!matchingSpareHistory.length) return null;

  const sameLocation = matchingSpareHistory
    .filter((entry) => entry.locationId === locationId)
    .sort((a, b) => new Date(b.purchasedAt) - new Date(a.purchasedAt))[0];

  if (sameLocation) {
    return {
      unitRate: sameLocation.unitRate,
      purchasedAt: sameLocation.purchasedAt,
      source: 'Same location last purchase'
    };
  }

  const otherLocation = matchingSpareHistory
    .sort((a, b) => new Date(b.purchasedAt) - new Date(a.purchasedAt))[0];

  return otherLocation
    ? {
        unitRate: otherLocation.unitRate,
        purchasedAt: otherLocation.purchasedAt,
        source: 'Other location last purchase'
      }
    : null;
};

const getRateMetadata = (batches, enteredAdjustmentQty) => {
  const direction = Number(enteredAdjustmentQty || 0);
  if (!direction) {
    return { basisLabel: 'No adjustment', appliedRate: null, needsManualRate: false, referenceDate: null };
  }

  if (!batches || !batches.length) {
    return {
      basisLabel: direction > 0 ? 'LIFO rate unavailable' : 'FIFO rate unavailable',
      appliedRate: null,
      needsManualRate: true,
      referenceDate: null
    };
  }

  const sorted = [...batches].sort((a, b) => new Date(a.receivedAt) - new Date(b.receivedAt));
  const chosen = direction > 0 ? sorted[sorted.length - 1] : sorted[0];

  return {
    basisLabel: direction > 0 ? 'LIFO' : 'FIFO',
    appliedRate: chosen.unitRate,
    needsManualRate: false,
    referenceDate: chosen.receivedAt
  };
};

const buildAdjustmentPlan = (row, countedSubLocationLabel) => {
  const subLocationEntries = row.inventorySubLocationDetails || [];

  if (!subLocationEntries.length) {
    return [
      {
        key: 'new-counted-stock',
        subLocationName: countedSubLocationLabel || 'Counted Location',
        systemQty: 0,
        countedQty: row.stockTakingQty,
        adjustmentQty: row.stockTakingQty,
        action: 'Create balance',
        batches: []
      }
    ];
  }

  if (row.delta > 0) {
    const primary = subLocationEntries[0];
    return subLocationEntries.map((entry, index) => ({
      key: `${row.spareId}-${entry.subLocationId}`,
      subLocationName: entry.subLocationName,
      systemQty: entry.quantity,
      countedQty: index === 0 ? entry.quantity + row.delta : entry.quantity,
      adjustmentQty: index === 0 ? row.delta : 0,
      action: index === 0 ? `Increase at ${entry.subLocationName}` : 'No change',
      batches: entry.batches || []
    }));
  }

  let remainingReduction = Math.abs(row.delta);
  return subLocationEntries.map((entry) => {
    const reduceBy = Math.min(entry.quantity, remainingReduction);
    remainingReduction -= reduceBy;
    return {
      key: `${row.spareId}-${entry.subLocationId}`,
      subLocationName: entry.subLocationName,
      systemQty: entry.quantity,
      countedQty: entry.quantity - reduceBy,
      adjustmentQty: reduceBy ? -reduceBy : 0,
      action: reduceBy ? `Reduce from ${entry.subLocationName}` : 'No change',
      batches: entry.batches || []
    };
  });
};

const SparesReconciliation = () => {
  const [vouchers, setVouchers] = useState(getStockTakingVouchers());
  const [filters, setFilters] = useState({
    date: null,
    locationId: undefined
  });
  const [selectedVoucherId, setSelectedVoucherId] = useState(getVoucherIdFromHash());
  const [ignoredMap, setIgnoredMap] = useState({});
  const [ignoreModal, setIgnoreModal] = useState({ visible: false, row: null, remark: '' });
  const [adjustmentModal, setAdjustmentModal] = useState({ visible: false, row: null, reason: '', allocations: [] });
  const [createdAdjustments, setCreatedAdjustments] = useState(getAdjustmentVouchers());
  const [adjustmentDetailsVoucher, setAdjustmentDetailsVoucher] = useState(null);

  const locations = useMemo(() => getLocations(), []);
  const spareMaster = useMemo(() => getSpareMaster(), []);
  const inventorySnapshot = useMemo(() => getInventorySnapshot(), []);
  const purchaseHistory = useMemo(() => getSparePurchaseHistory(), []);

  useEffect(() => {
    const unsubscribe = subscribeToSparesStore((nextVouchers, nextAdjustments) => {
      setVouchers(nextVouchers);
      setCreatedAdjustments(nextAdjustments || []);
    });

    const handleHashChange = () => {
      const voucherId = getVoucherIdFromHash();
      if (voucherId) setSelectedVoucherId(voucherId);
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => {
      unsubscribe();
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const submittedVouchers = useMemo(
    () => vouchers.filter((voucher) => voucher.status === 'Submitted'),
    [vouchers]
  );

  const filteredVoucherOptions = useMemo(() => {
    return submittedVouchers.filter((voucher) => {
      const matchesDate = filters.date ? voucher.date === filters.date.format('YYYY-MM-DD') : true;
      const matchesLocation = filters.locationId ? voucher.locationId === filters.locationId : true;
      return matchesDate && matchesLocation;
    });
  }, [filters.date, filters.locationId, submittedVouchers]);

  useEffect(() => {
    if (!submittedVouchers.length) {
      setSelectedVoucherId(null);
      return;
    }

    if (selectedVoucherId && submittedVouchers.some((voucher) => voucher.id === selectedVoucherId)) {
      return;
    }

    setSelectedVoucherId(filteredVoucherOptions[0]?.id || submittedVouchers[0].id);
  }, [filteredVoucherOptions, selectedVoucherId, submittedVouchers]);

  const selectedVoucher = useMemo(
    () => submittedVouchers.find((voucher) => voucher.id === selectedVoucherId) || null,
    [selectedVoucherId, submittedVouchers]
  );

  useEffect(() => {
    if (!selectedVoucher) return;
    setFilters({
      date: moment(selectedVoucher.date),
      locationId: selectedVoucher.locationId
    });
  }, [selectedVoucherId]);

  const selectedLocation = locations.find((location) => location.id === selectedVoucher?.locationId);
  const subLocationLabel = (subLocationId) =>
    selectedLocation?.subLocations.find((item) => item.id === subLocationId)?.name || '-';
  const spareLookup = (spareId) => spareMaster.find((spare) => spare.id === spareId);

  const reconciliation = useMemo(() => {
    if (!selectedVoucher) {
      return {
        foundRows: [],
        matchedOrVarianceRows: [],
        takingOnlyRows: [],
        inventoryOnlyRows: [],
        summary: { matched: 0, foundOnly: 0, inventoryOnly: 0, needsAction: 0 }
      };
    }

    const inventoryForLocation = inventorySnapshot.filter(
      (item) => item.locationId === selectedVoucher.locationId
    );

    const inventoryBySpare = inventoryForLocation.reduce((acc, item) => {
      if (!acc[item.spareId]) {
        acc[item.spareId] = { quantity: 0, subLocations: [], subLocationDetails: [] };
      }
      acc[item.spareId].quantity += item.quantity;
      acc[item.spareId].subLocations.push(`${subLocationLabel(item.subLocationId)} (${item.quantity})`);
      acc[item.spareId].subLocationDetails.push({
        subLocationId: item.subLocationId,
        subLocationName: subLocationLabel(item.subLocationId),
        quantity: item.quantity,
        batches: item.batches || []
      });
      return acc;
    }, {});

    const foundRows = selectedVoucher.lines.map((line) => {
      const spare = spareLookup(line.spareId);
      const inventory = inventoryBySpare[line.spareId];
      const inventoryQty = inventory ? inventory.quantity : 0;
      const delta = line.quantityFound - inventoryQty;
      const hasInventory = Boolean(inventory);
      const status = !hasInventory ? 'found-only' : delta === 0 ? 'matched' : 'variance';

      return {
        key: `found-${line.spareId}`,
        type: 'found',
        spareId: line.spareId,
        code: spare?.code || line.spareId,
        name: spare?.name || '-',
        category: spare?.category || '-',
        uom: spare?.uom || '-',
        stockTakingQty: line.quantityFound,
        inventoryQty,
        delta,
        status,
        inventorySubLocations: inventory ? inventory.subLocations.join(', ') : 'Not present in inventory',
        inventorySubLocationDetails: inventory ? inventory.subLocationDetails : [],
        ignored: Boolean(ignoredMap[`${selectedVoucher.id}:${line.spareId}`]?.remark),
        ignoreRemark: ignoredMap[`${selectedVoucher.id}:${line.spareId}`]?.remark || ''
      };
    });

    const matchedOrVarianceRows = foundRows.filter((row) => row.status !== 'found-only');
    const takingOnlyRows = foundRows.filter((row) => row.status === 'found-only');
    const foundSpareIds = new Set(selectedVoucher.lines.map((line) => line.spareId));

    const inventoryOnlyRows = Object.keys(inventoryBySpare)
      .filter((spareId) => !foundSpareIds.has(spareId))
      .map((spareId) => {
        const spare = spareLookup(spareId);
        const inventory = inventoryBySpare[spareId];
        return {
          key: `inventory-${spareId}`,
          type: 'inventory',
          spareId,
          code: spare?.code || spareId,
          name: spare?.name || '-',
          category: spare?.category || '-',
          uom: spare?.uom || '-',
          stockTakingQty: 0,
          inventoryQty: inventory.quantity,
          delta: -inventory.quantity,
          status: 'inventory-only',
          inventorySubLocations: inventory.subLocations.join(', '),
          inventorySubLocationDetails: inventory.subLocationDetails,
          ignored: Boolean(ignoredMap[`${selectedVoucher.id}:${spareId}`]?.remark),
          ignoreRemark: ignoredMap[`${selectedVoucher.id}:${spareId}`]?.remark || ''
        };
      });

    const matched = foundRows.filter((row) => row.status === 'matched').length;
    const needsAction =
      foundRows.filter((row) => row.status === 'variance' || row.status === 'found-only').filter((row) => !row.ignored).length +
      inventoryOnlyRows.filter((row) => !row.ignored).length;

      return {
        foundRows,
        matchedOrVarianceRows,
        takingOnlyRows,
        inventoryOnlyRows,
        ignoredRows: [...matchedOrVarianceRows, ...takingOnlyRows, ...inventoryOnlyRows].filter((row) => row.ignored),
        summary: {
        matched,
        foundOnly: takingOnlyRows.length,
        inventoryOnly: inventoryOnlyRows.length,
        needsAction
      }
    };
  }, [ignoredMap, inventorySnapshot, selectedVoucher, selectedLocation, spareMaster]);

  const adjustmentSummary = useMemo(() => {
    const scopedAdjustments = createdAdjustments.filter((voucher) => {
      if (selectedVoucher && voucher.stockTakingVoucherId !== selectedVoucher.id) return false;
      return true;
    });

    return scopedAdjustments.reduce(
      (acc, voucher) => {
        acc.voucherCount += 1;
        voucher.allocations.forEach((allocation) => {
          const amount = Number(allocation.adjustmentValue || 0);
          if (allocation.enteredAdjustmentQty > 0) {
            acc.addedAmount += amount;
          } else if (allocation.enteredAdjustmentQty < 0) {
            acc.reducedAmount += amount;
          }
        });
        return acc;
      },
      { voucherCount: 0, addedAmount: 0, reducedAmount: 0 }
    );
  }, [createdAdjustments, selectedVoucher]);

  const scopedAdjustmentVouchers = useMemo(
    () => createdAdjustments.filter((voucher) => voucher.stockTakingVoucherId === selectedVoucher?.id),
    [createdAdjustments, selectedVoucher]
  );

  const adjustmentVoucherBySpareId = useMemo(
    () =>
      scopedAdjustmentVouchers.reduce((acc, voucher) => {
        if (!acc[voucher.reconciliationSpareId]) {
          acc[voucher.reconciliationSpareId] = voucher;
        }
        return acc;
      }, {}),
    [scopedAdjustmentVouchers]
  );

  const adjustedSpareIds = useMemo(
    () => new Set(scopedAdjustmentVouchers.map((voucher) => voucher.reconciliationSpareId)),
    [scopedAdjustmentVouchers]
  );

  const getRowActionState = (row) => {
    const adjustedVoucher = adjustmentVoucherBySpareId[row.spareId] || null;
    const adjusted = Boolean(adjustedVoucher);
    const ignored = Boolean(row.ignored);
    const actionable = row.status !== 'matched';
    const pending = actionable && !adjusted && !ignored;
    return { adjusted, ignored, pending, adjustedVoucher };
  };

  const buildSectionSummary = (rows, options = {}) => {
    if (options.type === 'adjustment-vouchers') {
      const totalItems = scopedAdjustmentVouchers.length;
      return {
        totalItems,
        adjusted: totalItems,
        ignored: 0,
        pending: 0
      };
    }

    return rows.reduce(
      (acc, row) => {
        const actionState = getRowActionState(row);
        acc.totalItems += 1;
        if (actionState.adjusted) acc.adjusted += 1;
        if (actionState.ignored) acc.ignored += 1;
        if (actionState.pending) acc.pending += 1;
        return acc;
      },
      { totalItems: 0, adjusted: 0, ignored: 0, pending: 0 }
    );
  };

  const sectionSummaries = useMemo(() => {
    const sparesFound = buildSectionSummary(reconciliation.matchedOrVarianceRows);
    const stockTakingOnly = buildSectionSummary(reconciliation.takingOnlyRows);
    const inventoryOnly = buildSectionSummary(reconciliation.inventoryOnlyRows);
    const ignoredItems = buildSectionSummary(reconciliation.ignoredRows || []);
    const adjustmentVouchers = buildSectionSummary([], { type: 'adjustment-vouchers' });

    const overall = [sparesFound, stockTakingOnly, inventoryOnly].reduce(
      (acc, summary) => ({
        totalItems: acc.totalItems + summary.totalItems,
        adjusted: acc.adjusted + summary.adjusted,
        ignored: acc.ignored + summary.ignored,
        pending: acc.pending + summary.pending
      }),
      { totalItems: 0, adjusted: 0, ignored: 0, pending: 0 }
    );

    return {
      sparesFound,
      stockTakingOnly,
      inventoryOnly,
      ignoredItems,
      adjustmentVouchers,
      overall
    };
  }, [reconciliation, scopedAdjustmentVouchers, adjustedSpareIds, adjustmentVoucherBySpareId]);

  const openIgnoreModal = (row) => setIgnoreModal({ visible: true, row, remark: '' });
  const closeIgnoreModal = () => setIgnoreModal({ visible: false, row: null, remark: '' });

  const markIgnored = () => {
    if (!selectedVoucher || !ignoreModal.row) return;
    if (!String(ignoreModal.remark || '').trim()) {
      message.error('Remark is required before ignoring a reconciliation difference.');
      return;
    }
    setIgnoredMap((current) => ({
      ...current,
      [`${selectedVoucher.id}:${ignoreModal.row.spareId}`]: {
        remark: ignoreModal.remark.trim(),
        ignoredAt: new Date().toISOString()
      }
    }));
    message.success('Difference marked as ignored in this mock report.');
    closeIgnoreModal();
  };

  const openAdjustmentModal = (row) => {
    setAdjustmentModal({
      visible: true,
      row,
      reason: '',
      allocations: buildAdjustmentPlan(row, countedSubLocationName).map((entry) => ({
        ...entry,
        enteredAdjustmentQty: entry.adjustmentQty,
        fallbackRate: getFallbackPurchaseRate(purchaseHistory, row.spareId, selectedVoucher?.locationId),
        manualRate: getFallbackPurchaseRate(purchaseHistory, row.spareId, selectedVoucher?.locationId)?.unitRate || null
      }))
    });
  };

  const closeAdjustmentModal = () => setAdjustmentModal({ visible: false, row: null, reason: '', allocations: [] });
  const closeAdjustmentDetailsModal = () => setAdjustmentDetailsVoucher(null);

  const saveAdjustmentMock = () => {
    if (!adjustmentModal.row || !selectedVoucher) return;

    const valuedAllocations = adjustmentModal.allocations
      .filter((allocation) => allocation.enteredAdjustmentQty)
      .map((allocation) => {
        const metadata = getRateMetadata(allocation.batches, allocation.enteredAdjustmentQty);
        const appliedRate = metadata.needsManualRate ? allocation.manualRate : metadata.appliedRate;
        return {
          ...allocation,
          rateBasis: metadata.basisLabel,
          appliedRate,
          adjustmentValue: Math.abs(Number(allocation.enteredAdjustmentQty)) * Number(appliedRate || 0)
        };
      });

    const createdVoucher = createAdjustmentVoucher({
      stockTakingVoucherId: selectedVoucher.id,
      reconciliationSpareId: adjustmentModal.row.spareId,
      stockTakingVoucherNumber: selectedVoucher.voucherNumber,
      spareCode: adjustmentModal.row.code,
      spareName: adjustmentModal.row.name,
      locationId: selectedVoucher.locationId,
      reason: adjustmentModal.reason,
      allocations: valuedAllocations
    });

    message.success(`Mock spares adjustment voucher ${createdVoucher.voucherNumber} created.`);
    closeAdjustmentModal();
  };

  const baseColumns = [
    {
      title: tooltipLabel('Spare', 'Spare item being compared between stock taking and inventory.'),
      dataIndex: 'name',
      render: (value, record) => (
        <div>
          <div className="spares-cell-strong">{value}</div>
          <div className="spares-cell-subtle">{record.code} • {record.category}</div>
        </div>
      )
    },
    {
      title: tooltipLabel('UOM', 'Unit of measure from the spare master.'),
      dataIndex: 'uom',
      width: 90
    },
    {
      title: tooltipLabel('Stock Taking Qty', 'Quantity physically counted during the stock-taking exercise.'),
      dataIndex: 'stockTakingQty',
      align: 'right',
      width: 140
    },
    {
      title: tooltipLabel('Inventory Qty', 'System quantity aggregated across all sub-locations under the same location.'),
      dataIndex: 'inventoryQty',
      align: 'right',
      width: 120
    },
    {
      title: tooltipLabel('Difference', 'Stock taking quantity minus inventory quantity. Positive means more found physically; negative means inventory is higher.'),
      dataIndex: 'delta',
      align: 'right',
      width: 120,
      render: (delta) => (
        <span className={delta === 0 ? 'spares-delta-neutral' : delta > 0 ? 'spares-delta-positive' : 'spares-delta-negative'}>
          {delta > 0 ? '+' : ''}{delta}
        </span>
      )
    },
    {
      title: tooltipLabel('Inventory Footprint', 'Shows where this spare currently exists in inventory across sub-locations.'),
      dataIndex: 'inventorySubLocations',
      render: (value) => <span className="spares-cell-subtle">{value}</span>
    },
    {
      title: tooltipLabel('Status', 'Matched means quantities align. Other statuses need review, ignore, or adjustment.'),
      dataIndex: 'status',
      width: 120,
      render: (status, record) => {
        const actionState = getRowActionState(record);
        if (actionState.adjusted) {
          return (
            <Tooltip title={`Adjusted through ${actionState.adjustedVoucher?.voucherNumber}`}>
              <Tag color="blue">Adjusted</Tag>
            </Tooltip>
          );
        }
        if (record.ignored) return <Tag color="default">Ignored</Tag>;
        if (status === 'matched') return <Tag color="green">Matched</Tag>;
        if (status === 'variance') return <Tag color="orange">Variance</Tag>;
        if (status === 'found-only') return <Tag color="gold">Found Only</Tag>;
        return <Tag color="red">Inventory Only</Tag>;
      }
    }
  ];

  const actionColumn = {
    title: tooltipLabel('Actions', 'Ignore the difference or create a mock adjustment voucher for this spare.'),
    width: 170,
    render: (text, record) => {
      const actionState = getRowActionState(record);
      if (actionState.adjusted) {
        return (
          <Tooltip title={`This spare has already been adjusted with ${actionState.adjustedVoucher?.voucherNumber}.`}>
            <Tag color="blue">{actionState.adjustedVoucher?.voucherNumber || 'Adjusted'}</Tag>
          </Tooltip>
        );
      }
      const actionable = record.status !== 'matched' && !record.ignored;
      if (!actionable) return <Tag color="blue">Reviewed</Tag>;
      return (
        <div className="spares-row-actions">
          <Button size="small" onClick={() => openIgnoreModal(record)}>
            Ignore
          </Button>
          <Button size="small" type="primary" ghost onClick={() => openAdjustmentModal(record)}>
            Adjust
          </Button>
        </div>
      );
    }
  };

  const countedSubLocationName = selectedVoucher?.subLocationId ? subLocationLabel(selectedVoucher.subLocationId) : 'All counted sub-locations';
  const adjustmentPlanColumns = [
    {
      title: tooltipLabel('Sub Location', 'Specific rack, bay, or storage area where the adjustment will be posted.'),
      dataIndex: 'subLocationName',
      width: 140
    },
    {
      title: tooltipLabel('System Qty', 'Quantity currently available in inventory for this sub-location.'),
      dataIndex: 'systemQty',
      align: 'right',
      width: 90
    },
    {
      title: tooltipLabel('Current / Proposed Qty', 'Illustrative quantity after applying the current mock adjustment suggestion.'),
      dataIndex: 'countedQty',
      align: 'right',
      width: 130
    },
    {
      title: tooltipLabel('Enter Adjustment', 'Enter how much stock to add or reduce at this sub-location.'),
      dataIndex: 'enteredAdjustmentQty',
      width: 135,
      render: (value, record) => (
        <InputNumber
          value={value}
          style={{ width: '100%' }}
          onChange={(nextValue) => {
            setAdjustmentModal((current) => ({
              ...current,
              allocations: current.allocations.map((allocation) =>
                allocation.key === record.key
                  ? { ...allocation, enteredAdjustmentQty: nextValue || 0 }
                  : allocation
              )
            }));
          }}
        />
      )
    },
    {
      title: tooltipLabel('Rate Basis', 'Increase uses LIFO and decrease uses FIFO. Manual rate is needed when no historical rate exists.'),
      width: 165,
      render: (text, record) => {
        const metadata = getRateMetadata(record.batches, record.enteredAdjustmentQty);
        if (!record.enteredAdjustmentQty) return <span className="spares-cell-subtle">No adjustment</span>;
        return (
          <div>
            <div className="spares-cell-strong">{metadata.basisLabel}</div>
            <div className="spares-cell-subtle">
              {metadata.referenceDate ? moment(metadata.referenceDate).format('DD MMM YYYY') : 'Manual rate required'}
            </div>
          </div>
        );
      }
    },
    {
      title: tooltipLabel('Applied Rate', 'Rate used to value the adjustment line. Enter manually when no prior rate exists.'),
      width: 145,
      render: (text, record) => {
        const metadata = getRateMetadata(record.batches, record.enteredAdjustmentQty);
        const appliedRate = metadata.needsManualRate ? record.manualRate : metadata.appliedRate;

        if (!record.enteredAdjustmentQty) {
          return <span className="spares-cell-subtle">-</span>;
        }

        if (metadata.needsManualRate) {
          return (
            <div>
              <InputNumber
                min={0}
                value={record.manualRate}
                style={{ width: '100%' }}
                placeholder="Rate"
                onChange={(nextValue) => {
                  setAdjustmentModal((current) => ({
                    ...current,
                    allocations: current.allocations.map((allocation) =>
                      allocation.key === record.key
                        ? { ...allocation, manualRate: nextValue || null }
                        : allocation
                    )
                  }));
                }}
              />
              {record.fallbackRate && (
                <div className="spares-cell-subtle">
                  Suggested: {formatCurrency(record.fallbackRate.unitRate)} • {record.fallbackRate.source} • {moment(record.fallbackRate.purchasedAt).format('DD MMM YYYY')}
                </div>
              )}
            </div>
          );
        }

        return <span className="spares-cell-strong">{formatCurrency(appliedRate)}</span>;
      }
    },
    {
      title: tooltipLabel('Mock Action', 'Narrative explanation of whether this line increases, reduces, or leaves the sub-location unchanged.'),
      width: 150,
      render: (text, record) => {
        const value = record.enteredAdjustmentQty || 0;
        if (value > 0) return `Increase at ${record.subLocationName}`;
        if (value < 0) return `Reduce from ${record.subLocationName}`;
        return 'No change';
      }
    },
    {
      title: tooltipLabel('Resulting Qty', 'Inventory quantity that would remain in this sub-location after the entered adjustment.'),
      width: 120,
      align: 'right',
      render: (text, record) => {
        const entered = record.enteredAdjustmentQty || 0;
        const resultingQty = Number(record.systemQty) + Number(entered);
        return (
          <span className={resultingQty === record.systemQty ? 'spares-delta-neutral' : resultingQty > record.systemQty ? 'spares-delta-positive' : 'spares-delta-negative'}>
            {resultingQty}
          </span>
        );
      }
    },
    {
      title: tooltipLabel('Adjustment Value', 'Monetary value of this line based on the entered quantity and applied rate.'),
      width: 145,
      align: 'right',
      render: (text, record) => {
        const metadata = getRateMetadata(record.batches, record.enteredAdjustmentQty);
        const appliedRate = metadata.needsManualRate ? record.manualRate : metadata.appliedRate;
        if (!record.enteredAdjustmentQty || !appliedRate) {
          return <span className="spares-cell-subtle">Pending rate</span>;
        }

        return (
          <span className="spares-cell-strong">
            {formatCurrency(Math.abs(record.enteredAdjustmentQty) * appliedRate)}
          </span>
        );
      }
    }
  ];

  const missingRates = adjustmentModal.allocations.filter((allocation) => {
    const metadata = getRateMetadata(allocation.batches, allocation.enteredAdjustmentQty);
    return allocation.enteredAdjustmentQty && metadata.needsManualRate && !allocation.manualRate;
  });

  const rateSummaryText = adjustmentModal.allocations
    .filter((allocation) => allocation.enteredAdjustmentQty)
    .map((allocation) => {
      const metadata = getRateMetadata(allocation.batches, allocation.enteredAdjustmentQty);
      const appliedRate = metadata.needsManualRate ? allocation.manualRate : metadata.appliedRate;
      if (!appliedRate) {
        if (allocation.fallbackRate) {
          return `${allocation.subLocationName}: ${allocation.fallbackRate.source} ${formatCurrency(allocation.fallbackRate.unitRate)}`;
        }
        return `${allocation.subLocationName}: rate required`;
      }
      return `${allocation.subLocationName}: ${metadata.basisLabel} at ${formatCurrency(appliedRate)}`;
    })
    .join(' • ');

  const handleSaveAdjustment = () => {
    if (missingRates.length) {
      message.error('Enter a rate for all adjustment rows where no prior stock rate exists.');
      return;
    }
    saveAdjustmentMock();
  };

  const adjustmentDetailsColumns = [
    {
      title: tooltipLabel('Sub Location', 'Sub-location where the adjustment was posted.'),
      dataIndex: 'subLocationName'
    },
    {
      title: tooltipLabel('Adjustment Qty', 'Quantity increased or reduced for this sub-location.'),
      dataIndex: 'enteredAdjustmentQty',
      align: 'right',
      width: 120,
      render: (value) => (
        <span className={value === 0 ? 'spares-delta-neutral' : value > 0 ? 'spares-delta-positive' : 'spares-delta-negative'}>
          {value > 0 ? '+' : ''}{value}
        </span>
      )
    },
    {
      title: tooltipLabel('Rate Basis', 'Valuation method used for this adjustment row.'),
      dataIndex: 'rateBasis',
      width: 120
    },
    {
      title: tooltipLabel('Applied Rate', 'Unit rate used to value the adjustment.'),
      dataIndex: 'appliedRate',
      align: 'right',
      width: 130,
      render: (value) => formatCurrency(value)
    },
    {
      title: tooltipLabel('Value', 'Monetary value of the adjustment row.'),
      dataIndex: 'adjustmentValue',
      align: 'right',
      width: 140,
      render: (value) => formatCurrency(value)
    }
  ];

  return (
    <div className="spares-page">
      <div className="spares-hero">
        <div>
          <div className="spares-eyebrow">Variance Resolution Mock</div>
          <h3>Reconcile counted spares against inventory across the same location and all sub-locations.</h3>
          <p>
            This report includes matched lines, items found only during stock taking, and items still sitting in inventory but not physically counted.
          </p>
        </div>
      </div>

      <Card className="spares-panel-card spares-filter-card" title={tooltipLabel('Reconciliation Filters', 'Choose the submitted stock-taking voucher context used to compare counted spares against system inventory.')}>
        <div className="spares-form-grid">
          <div className="spares-form-field">
            <span>{tooltipLabel('Date', 'Filters submitted vouchers by stock-taking date.')}</span>
            <DatePicker
              value={filters.date}
              onChange={(value) => setFilters((current) => ({ ...current, date: value }))}
              style={{ width: '100%' }}
            />
          </div>
          <div className="spares-form-field">
            <span>{tooltipLabel('Location', 'Filters vouchers and reconciliation results to a selected location.')}</span>
            <Select
              allowClear
              value={filters.locationId}
              placeholder="Filter by location"
              onChange={(value) => setFilters((current) => ({ ...current, locationId: value }))}
            >
              {locations.map((location) => (
                <Option key={location.id} value={location.id}>
                  {location.name}
                </Option>
              ))}
            </Select>
          </div>
          <div className="spares-form-field">
            <span>{tooltipLabel('Source Voucher', 'Submitted stock-taking voucher used as the base for this reconciliation.')}</span>
            <Select
              value={selectedVoucherId || undefined}
              placeholder="Select submitted voucher"
              onChange={setSelectedVoucherId}
            >
              {filteredVoucherOptions.map((voucher) => {
                const location = locations.find((item) => item.id === voucher.locationId);
                return (
                  <Option key={voucher.id} value={voucher.id}>
                    {voucher.voucherNumber} • {location?.name || '-'}
                  </Option>
                );
              })}
            </Select>
          </div>
        </div>
      </Card>

      {!selectedVoucher ? (
        <Card className="spares-panel-card">
          <Empty description="Select a submitted stock-taking voucher to generate the reconciliation mock." />
        </Card>
      ) : (
        <div>
          <Alert
            type="info"
            showIcon
            className="spares-alert"
            message={`Reconciling ${selectedVoucher.voucherNumber}`}
            description={`Location: ${selectedLocation?.name || '-'}${selectedVoucher.subLocationId ? ` • Counted in ${subLocationLabel(selectedVoucher.subLocationId)}` : ''} • Inventory includes all sub-locations in this location, so the same spare can appear in multiple bins/racks and be adjusted between them.`}
          />

          <Row gutter={16} className="spares-stat-row">
            <Col xs={24} xl={8}>
              <Card className="spares-stat-card">
                <div className="spares-summary-card-title">{tooltipLabel('Spares Found', 'Items present in both stock taking and inventory, whether quantities match or not.')}</div>
                <div className="spares-summary-grid">
                  <div><span>Total</span><strong>{sectionSummaries.sparesFound.totalItems}</strong></div>
                  <div><span>Adjusted</span><strong>{sectionSummaries.sparesFound.adjusted}</strong></div>
                  <div><span>Ignored</span><strong>{sectionSummaries.sparesFound.ignored}</strong></div>
                  <div><span>Pending</span><strong>{sectionSummaries.sparesFound.pending}</strong></div>
                </div>
              </Card>
            </Col>
            <Col xs={24} xl={8}>
              <Card className="spares-stat-card">
                <div className="spares-summary-card-title">{tooltipLabel('Stock Taking Only', 'Items counted physically but not present in inventory for this location.')}</div>
                <div className="spares-summary-grid">
                  <div><span>Total</span><strong>{sectionSummaries.stockTakingOnly.totalItems}</strong></div>
                  <div><span>Adjusted</span><strong>{sectionSummaries.stockTakingOnly.adjusted}</strong></div>
                  <div><span>Ignored</span><strong>{sectionSummaries.stockTakingOnly.ignored}</strong></div>
                  <div><span>Pending</span><strong>{sectionSummaries.stockTakingOnly.pending}</strong></div>
                </div>
              </Card>
            </Col>
            <Col xs={24} xl={8}>
              <Card className="spares-stat-card">
                <div className="spares-summary-card-title">{tooltipLabel('Inventory Only', 'Items present in inventory but not counted in the selected stock-taking voucher.')}</div>
                <div className="spares-summary-grid">
                  <div><span>Total</span><strong>{sectionSummaries.inventoryOnly.totalItems}</strong></div>
                  <div><span>Adjusted</span><strong>{sectionSummaries.inventoryOnly.adjusted}</strong></div>
                  <div><span>Ignored</span><strong>{sectionSummaries.inventoryOnly.ignored}</strong></div>
                  <div><span>Pending</span><strong>{sectionSummaries.inventoryOnly.pending}</strong></div>
                </div>
              </Card>
            </Col>
          </Row>

          <Row gutter={16} className="spares-stat-row">
            <Col xs={24} xl={12}>
              <Card className="spares-stat-card spares-stat-card-accent">
                <div className="spares-summary-card-title">{tooltipLabel('Overall Reconciliation', 'Combined action status across the first three reconciliation tabs.')}</div>
                <div className="spares-summary-grid">
                  <div><span>Total Items</span><strong>{sectionSummaries.overall.totalItems}</strong></div>
                  <div><span>Total Adjusted</span><strong>{sectionSummaries.overall.adjusted}</strong></div>
                  <div><span>Total Ignored</span><strong>{sectionSummaries.overall.ignored}</strong></div>
                  <div><span>Total Pending</span><strong>{sectionSummaries.overall.pending}</strong></div>
                </div>
              </Card>
            </Col>
            <Col xs={24} xl={12}>
              <Card className="spares-stat-card">
                <div className="spares-summary-card-title">{tooltipLabel('Adjustment Value', 'Financial summary of created mock adjustment vouchers for this reconciliation.')}</div>
                <div className="spares-summary-grid">
                  <div><span>Vouchers</span><strong>{adjustmentSummary.voucherCount}</strong></div>
                  <div><span>Added</span><strong>{formatCurrency(adjustmentSummary.addedAmount)}</strong></div>
                  <div><span>Reduced</span><strong>{formatCurrency(adjustmentSummary.reducedAmount)}</strong></div>
                  <div><span>Net</span><strong>{formatCurrency(adjustmentSummary.addedAmount - adjustmentSummary.reducedAmount)}</strong></div>
                </div>
              </Card>
            </Col>
          </Row>

          <Card className="spares-panel-card spares-tabs-card" title={tooltipLabel('Reconciliation Sections', 'Each tab isolates one reconciliation outcome so the user can review and act on it clearly.')}>
            <Tabs defaultActiveKey="spares-found" animated={false}>
              <TabPane
                tab={<span>{tooltipLabel(`Spares Found (${reconciliation.matchedOrVarianceRows.length})`, 'Items existing in both stock taking and inventory. Quantities may still differ.')}</span>}
                key="spares-found"
              >
                <Table
                  rowKey="key"
                  dataSource={reconciliation.matchedOrVarianceRows}
                  columns={[...baseColumns, actionColumn]}
                  pagination={false}
                  locale={{ emptyText: 'No common spares found between stock taking and inventory for this selection.' }}
                />
              </TabPane>
              <TabPane
                tab={<span>{tooltipLabel(`Stock Taking Only (${reconciliation.takingOnlyRows.length})`, 'Items counted physically but missing from inventory.')}</span>}
                key="stock-taking-only"
              >
                <Table
                  rowKey="key"
                  dataSource={reconciliation.takingOnlyRows}
                  columns={[...baseColumns, actionColumn]}
                  pagination={false}
                  locale={{ emptyText: 'No stock-taking-only spares in this mock report.' }}
                />
              </TabPane>
              <TabPane
                tab={<span>{tooltipLabel(`Inventory Only (${reconciliation.inventoryOnlyRows.length})`, 'Items existing in inventory but not found during stock taking.')}</span>}
                key="inventory-only"
              >
                <Table
                  rowKey="key"
                  dataSource={reconciliation.inventoryOnlyRows}
                  columns={[...baseColumns, actionColumn]}
                  pagination={false}
                  locale={{ emptyText: 'No inventory-only spares in this mock report.' }}
                />
              </TabPane>
              <TabPane
                tab={<span>{tooltipLabel(`Ignored Spares (${(reconciliation.ignoredRows || []).length})`, 'Items explicitly ignored during reconciliation along with the captured remarks.')}</span>}
                key="ignored-spares"
              >
                <Table
                  rowKey="key"
                  dataSource={reconciliation.ignoredRows || []}
                  pagination={false}
                  locale={{ emptyText: 'No spares have been ignored in this reconciliation yet.' }}
                  columns={[
                    {
                      title: tooltipLabel('Spare', 'Ignored spare item.'),
                      render: (text, record) => (
                        <div>
                          <div className="spares-cell-strong">{record.name}</div>
                          <div className="spares-cell-subtle">{record.code} • {record.category}</div>
                        </div>
                      )
                    },
                    {
                      title: tooltipLabel('Tab Source', 'Original reconciliation section where this spare came from.'),
                      width: 150,
                      render: (text, record) => {
                        if (record.status === 'found-only') return 'Stock Taking Only';
                        if (record.status === 'inventory-only') return 'Inventory Only';
                        return 'Spares Found';
                      }
                    },
                    {
                      title: tooltipLabel('Difference', 'Difference that was ignored for this spare.'),
                      dataIndex: 'delta',
                      align: 'right',
                      width: 120,
                      render: (value) => (
                        <span className={value === 0 ? 'spares-delta-neutral' : value > 0 ? 'spares-delta-positive' : 'spares-delta-negative'}>
                          {value > 0 ? '+' : ''}{value}
                        </span>
                      )
                    },
                    {
                      title: tooltipLabel('Ignore Remark', 'Compulsory remark captured when the spare was ignored.'),
                      dataIndex: 'ignoreRemark'
                    }
                  ]}
                />
              </TabPane>
              <TabPane
                tab={<span>{tooltipLabel(`Adjustment Vouchers (${scopedAdjustmentVouchers.length})`, 'Mock adjustment vouchers created from reconciliation actions.')}</span>}
                key="adjustment-vouchers"
              >
                <Table
                  rowKey="id"
                  size="small"
                  pagination={false}
                  dataSource={scopedAdjustmentVouchers}
                  locale={{ emptyText: 'No adjustment vouchers created yet for this reconciliation.' }}
                  columns={[
                    {
                      title: tooltipLabel('Voucher', 'Adjustment voucher number created from reconciliation. Click to view full adjustment details.'),
                      dataIndex: 'voucherNumber',
                      width: 140,
                      render: (value, record) => (
                        <button type="button" className="spares-link-button" onClick={() => setAdjustmentDetailsVoucher(record)}>
                          {value}
                        </button>
                      )
                    },
                    {
                      title: tooltipLabel('Created On', 'Date and time when the mock adjustment voucher was created.'),
                      dataIndex: 'createdAt',
                      width: 150,
                      render: (value) => moment(value).format('DD MMM YYYY HH:mm')
                    },
                    {
                      title: tooltipLabel('Spare', 'Spare item adjusted by this voucher.'),
                      render: (text, record) => (
                        <div>
                          <div className="spares-cell-strong">{record.spareName}</div>
                          <div className="spares-cell-subtle">{record.spareCode}</div>
                        </div>
                      )
                    },
                    {
                      title: tooltipLabel('Added Amount', 'Total increase value posted by this voucher.'),
                      width: 140,
                      align: 'right',
                      render: (text, record) =>
                        formatCurrency(
                          record.allocations
                            .filter((allocation) => allocation.enteredAdjustmentQty > 0)
                            .reduce((sum, allocation) => sum + Number(allocation.adjustmentValue || 0), 0)
                        )
                    },
                    {
                      title: tooltipLabel('Reduced Amount', 'Total reduction value posted by this voucher.'),
                      width: 140,
                      align: 'right',
                      render: (text, record) =>
                        formatCurrency(
                          record.allocations
                            .filter((allocation) => allocation.enteredAdjustmentQty < 0)
                            .reduce((sum, allocation) => sum + Number(allocation.adjustmentValue || 0), 0)
                        )
                    }
                  ]}
                />
              </TabPane>
            </Tabs>
          </Card>
        </div>
      )}

      <Modal
        visible={ignoreModal.visible}
        title={tooltipLabel('Ignore Reconciliation Difference', 'Provide a compulsory remark before marking this spare as ignored.')}
        onCancel={closeIgnoreModal}
        onOk={markIgnored}
        okText="Ignore with Remark"
      >
        {ignoreModal.row ? (
          <div className="spares-adjustment-modal">
            <div className="spares-adjustment-grid">
              <div>
                <span>{tooltipLabel('Spare', 'Spare item being ignored in reconciliation.')}</span>
                <strong>{ignoreModal.row.code} • {ignoreModal.row.name}</strong>
              </div>
              <div>
                <span>{tooltipLabel('Difference', 'Difference being accepted without adjustment.')}</span>
                <strong>{ignoreModal.row.delta > 0 ? '+' : ''}{ignoreModal.row.delta} {ignoreModal.row.uom}</strong>
              </div>
            </div>
            <div className="spares-form-field">
              <span>{tooltipLabel('Ignore Remark', 'Compulsory remark explaining why this reconciliation difference is being ignored.')}</span>
              <TextArea
                rows={4}
                value={ignoreModal.remark}
                onChange={(event) => {
                  const { value } = event.target;
                  setIgnoreModal((current) => ({ ...current, remark: value }));
                }}
                placeholder="Example: Quantity difference accepted after site verification and no adjustment required."
              />
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        visible={adjustmentModal.visible}
        title={tooltipLabel('Mock Spares Adjustment Voucher', 'Create a mock adjustment by entering quantity changes per sub-location and valuing them using FIFO or LIFO logic.')}
        width={960}
        wrapClassName="spares-adjustment-modal-wrap"
        onCancel={closeAdjustmentModal}
        onOk={handleSaveAdjustment}
        okText="Prepare Adjustment Voucher"
      >
        {adjustmentModal.row && selectedVoucher ? (
          <div className="spares-adjustment-modal">
            <div className="spares-adjustment-grid">
              <div>
                <span>{tooltipLabel('Voucher', 'Source stock-taking voucher that triggered this adjustment.')}</span>
                <strong>{selectedVoucher.voucherNumber}</strong>
              </div>
              <div>
                <span>{tooltipLabel('Spare', 'Spare item currently being adjusted.')}</span>
                <strong>{adjustmentModal.row.code} • {adjustmentModal.row.name}</strong>
              </div>
              <div>
                <span>{tooltipLabel('Location', 'Parent location where this reconciliation and adjustment are being performed.')}</span>
                <strong>{selectedLocation?.name || '-'}</strong>
              </div>
              <div>
                <span>{tooltipLabel('Sub Location Context', 'Original counted sub-location context from the stock-taking voucher, if one was specified.')}</span>
                <strong>{selectedVoucher.subLocationId ? subLocationLabel(selectedVoucher.subLocationId) : 'All counted sub-locations'}</strong>
              </div>
              <div>
                <span>{tooltipLabel('System Qty', 'Total inventory quantity currently available before the adjustment.')}</span>
                <strong>{adjustmentModal.row.inventoryQty} {adjustmentModal.row.uom}</strong>
              </div>
              <div>
                <span>{tooltipLabel('Counted Qty', 'Total quantity found physically during stock taking.')}</span>
                <strong>{adjustmentModal.row.stockTakingQty} {adjustmentModal.row.uom}</strong>
              </div>
              <div>
                <span>{tooltipLabel('Adjustment Delta', 'Difference between counted quantity and inventory quantity that needs action.')}</span>
                <strong>{adjustmentModal.row.delta > 0 ? '+' : ''}{adjustmentModal.row.delta} {adjustmentModal.row.uom}</strong>
              </div>
              <div>
                <span>{tooltipLabel('Inventory Footprint', 'Sub-locations where the spare currently exists in inventory.')}</span>
                <strong>{adjustmentModal.row.inventorySubLocations}</strong>
              </div>
            </div>

            <Card
              size="small"
              className="spares-suballocation-card"
              title={tooltipLabel('Sub-location Adjustment Distribution', 'Distribute the adjustment across sub-locations and see the valuation basis applied to each row.')}
            >
              <div className="spares-suballocation-note">
                Enter the stock adjustment against each sub-location to show where the variance should be posted.
              </div>
              <Alert
                type="info"
                showIcon
                className="spares-rate-alert"
                message="Rate logic"
                description={rateSummaryText || 'Increase uses LIFO rates, decrease uses FIFO rates, and rows without prior stock history require a manual rate.'}
              />
              <Table
                rowKey="key"
                size="small"
                pagination={false}
                scroll={{ x: 1180 }}
                columns={adjustmentPlanColumns}
                dataSource={adjustmentModal.allocations}
              />
            </Card>

            <div className="spares-form-field">
              <span>{tooltipLabel('Adjustment Reason', 'Short explanation for why this adjustment voucher is being created.')}</span>
              <TextArea
                rows={4}
                value={adjustmentModal.reason}
                onChange={(event) => {
                  const { value } = event.target;
                  setAdjustmentModal((current) => ({ ...current, reason: value }));
                }}
                placeholder="Example: Physical count verified during stock taking and adjustment required to align stock."
              />
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        visible={Boolean(adjustmentDetailsVoucher)}
        title={adjustmentDetailsVoucher ? tooltipLabel(`Adjustment Voucher • ${adjustmentDetailsVoucher.voucherNumber}`, 'Read-only details of the created mock adjustment voucher.') : 'Adjustment Voucher'}
        footer={<Button onClick={closeAdjustmentDetailsModal}>Close</Button>}
        width={880}
        onCancel={closeAdjustmentDetailsModal}
      >
        {adjustmentDetailsVoucher ? (
          <div className="spares-adjustment-modal">
            <div className="spares-adjustment-grid">
              <div>
                <span>{tooltipLabel('Voucher', 'Adjustment voucher number.')}</span>
                <strong>{adjustmentDetailsVoucher.voucherNumber}</strong>
              </div>
              <div>
                <span>{tooltipLabel('Created On', 'Date and time when this adjustment voucher was generated.')}</span>
                <strong>{moment(adjustmentDetailsVoucher.createdAt).format('DD MMM YYYY HH:mm')}</strong>
              </div>
              <div>
                <span>{tooltipLabel('Stock Taking Voucher', 'Source stock-taking voucher that led to this adjustment.')}</span>
                <strong>{adjustmentDetailsVoucher.stockTakingVoucherNumber}</strong>
              </div>
              <div>
                <span>{tooltipLabel('Spare', 'Spare item covered by this adjustment voucher.')}</span>
                <strong>{adjustmentDetailsVoucher.spareCode} • {adjustmentDetailsVoucher.spareName}</strong>
              </div>
              <div>
                <span>{tooltipLabel('Added Value', 'Total value of all positive adjustment rows in this voucher.')}</span>
                <strong>{formatCurrency(adjustmentDetailsVoucher.allocations.filter((item) => item.enteredAdjustmentQty > 0).reduce((sum, item) => sum + Number(item.adjustmentValue || 0), 0))}</strong>
              </div>
              <div>
                <span>{tooltipLabel('Reduced Value', 'Total value of all negative adjustment rows in this voucher.')}</span>
                <strong>{formatCurrency(adjustmentDetailsVoucher.allocations.filter((item) => item.enteredAdjustmentQty < 0).reduce((sum, item) => sum + Number(item.adjustmentValue || 0), 0))}</strong>
              </div>
            </div>

            <Card
              size="small"
              className="spares-suballocation-card"
              title={tooltipLabel('Adjustment Lines', 'Detailed sub-location lines included in this created adjustment voucher.')}
            >
              <Table
                rowKey="key"
                size="small"
                pagination={false}
                columns={adjustmentDetailsColumns}
                dataSource={adjustmentDetailsVoucher.allocations}
              />
            </Card>

            <div className="spares-form-field">
              <span>{tooltipLabel('Adjustment Reason', 'User-entered note captured when the adjustment voucher was created.')}</span>
              <div className="spares-readonly-note">{adjustmentDetailsVoucher.reason || 'No reason entered in this mock voucher.'}</div>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default SparesReconciliation;
